import React, { useContext, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';

// Icons
import Icon from '@mdi/react';
import {
  mdiLoginVariant,
  mdiBookOpenPageVariantOutline,
  mdiYoutube,
  mdiFileDownloadOutline,
} from '@mdi/js';

import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../utils/axios';
import LoginOAuth2 from '@okteto/react-oauth2-login';

const MANUAL_URL =
  'https://drive.google.com/file/d/15J4FJN8fVeN9MCZHXUk__N6CGExXkDLF/view?usp=sharing';
const TUTORIAL_APPLY_URL = 'https://www.youtube.com/watch?v=SnQKJJBw9ho';
const TUTORIAL_CERTIFY_URL = 'https://www.youtube.com/watch?v=HcB2-dYuMSI';

// The popup library rejects with this message when the window is closed or
// was never opened (pop-up blocked). It is not a login error.
const POPUP_CLOSED = 'The popup was closed';

const openInNewTab = (url) => {
  const newTab = window.open(url, '_blank', 'noopener,noreferrer');
  if (newTab) newTab.opener = null;
};

// A single resource/help link rendered as a subtle, full-width row
// (same design as smepps-fe-react19).
const ResourceLink = ({ icon, iconColor, label, onClick }) => (
  <button type="button" onClick={onClick} className="signin-resource-link">
    <span className="signin-resource-icon" style={{ color: iconColor }}>
      <Icon path={icon} size={0.8} />
    </span>
    <span className="signin-resource-label">{label}</span>
  </button>
);

function SignIn() {
  const { signIn } = useContext(AuthContext);

  // idle → popup (waiting for the user in the popup) → verifying (token
  // exchange and user fetch). signIn() then shows the full-page loader.
  const [stage, setStage] = useState('idle');
  const [message, setMessage] = useState(null);

  // These env vars are historically named REACT_APP_ARCGIS_* but actually
  // point to the SMEPPS Laravel Passport OAuth2 server (NOT ArcGIS Portal).
  // Read the new REACT_APP_OAUTH_* names if present, fall back to the old
  // misnamed ones so deploys can roll forward without coordinating .env edits.
  const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID;
  const clientSecret = process.env.REACT_APP_OAUTH_CLIENT_SECRET;
  const authorizeUri = process.env.REACT_APP_OAUTH_AUTHORIZE_URI;
  const tokenUri = process.env.REACT_APP_OAUTH_TOKEN_URI;
  const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
  const registrationForm = process.env.REACT_APP_USER_REGISTRATION_FORM;

  const onRequest = () => {
    setMessage(null);
    setStage('popup');
  };

  const onSuccess = async (response) => {
    setStage('verifying');
    try {
      const tokenResponse = await axios.post(
        tokenUri,
        new URLSearchParams({
          code: response.code,
          client_id: clientId,
          client_secret: clientSecret,
          redirect_uri: redirectUri,
          grant_type: 'authorization_code',
        }).toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      const token = tokenResponse.data.access_token;

      localStorage.setItem('accessToken', token);

      const userResponse = await axios.get('/user', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await signIn(userResponse.data.user, token);
    } catch (error) {
      console.error('Error exchanging code for token:', error);
      setStage('idle');
      setMessage({
        variant: 'danger',
        text: 'Log masuk tidak berjaya. Sila cuba lagi. Jika masalah berterusan, hubungi pentadbir sistem.',
      });
    }
  };

  const onFailure = (error) => {
    setStage('idle');
    if (error?.message === POPUP_CLOSED) {
      setMessage({
        variant: 'secondary',
        text: 'Tetingkap log masuk telah ditutup. Jika tetingkap tidak muncul, benarkan pop-up bagi laman ini dan cuba lagi.',
      });
      return;
    }
    console.error('OAuth2 Login Failed:', error);
    setMessage({
      variant: 'danger',
      text: 'Log masuk tidak berjaya. Sila cuba lagi.',
    });
  };

  const busy = stage !== 'idle';

  return (
    <React.Fragment>
      {/* Scoped styles for the resource links (no SCSS rebuild needed) */}
      <style>{`
        .signin-resource-link {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          text-align: left;
          padding: 9px 12px;
          border: 1px solid transparent;
          border-radius: 10px;
          background: transparent;
          color: #495057;
          font-size: 13px;
          line-height: 1.3;
          transition: background-color .15s ease, border-color .15s ease, transform .15s ease;
        }
        .signin-resource-link:hover {
          background-color: #f1f4f9;
          border-color: #e2e8f0;
          transform: translateX(2px);
        }
        .signin-resource-icon { flex-shrink: 0; display: inline-flex; }
        .signin-resource-label { font-weight: 500; }
      `}</style>

      <div className="text-center mb-3">
        <h5 className="fw-bold mb-1">Selamat Datang</h5>
        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
          Sila log masuk untuk meneruskan ke sistem
        </p>
      </div>

      {message && (
        <Alert
          variant={message.variant}
          dismissible
          onClose={() => setMessage(null)}
          className="small"
        >
          <div className="alert-message">{message.text}</div>
        </Alert>
      )}

      <Button
        size="lg"
        variant="primary"
        as={LoginOAuth2}
        className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold py-2"
        clientId={clientId}
        authorizeUri={authorizeUri}
        redirectUri={redirectUri}
        scope="*"
        disabled={busy}
        onRequest={onRequest}
        onSuccess={onSuccess}
        onFailure={onFailure}
      >
        {busy ? (
          <>
            <Spinner animation="border" size="sm" />
            {stage === 'popup' ? 'Menunggu log masuk…' : 'Mengesahkan…'}
          </>
        ) : (
          <>
            <Icon path={mdiLoginVariant} size={0.9} />
            Log Masuk
          </>
        )}
      </Button>
      {stage === 'popup' && (
        <p className="text-muted text-center mt-2 mb-0" style={{ fontSize: 12 }}>
          Lengkapkan log masuk dalam tetingkap yang dibuka.
        </p>
      )}

      {/* Divider */}
      <div className="d-flex align-items-center my-4">
        <hr className="flex-grow-1 my-0" />
        <span
          className="px-3 text-muted text-uppercase"
          style={{ fontSize: 11, letterSpacing: 1, fontWeight: 600 }}
        >
          Panduan &amp; Sumber
        </span>
        <hr className="flex-grow-1 my-0" />
      </div>

      <div className="d-flex flex-column gap-1">
        <ResourceLink
          icon={mdiBookOpenPageVariantOutline}
          iconColor="#118AB2"
          label="Garis Panduan Penyediaan RMKe-13 Negeri Sabah"
          onClick={() => openInNewTab(MANUAL_URL)}
        />
        <ResourceLink
          icon={mdiYoutube}
          iconColor="#e53935"
          label="Tutorial: Permohonan Program / Projek"
          onClick={() => openInNewTab(TUTORIAL_APPLY_URL)}
        />
        <ResourceLink
          icon={mdiYoutube}
          iconColor="#e53935"
          label="Tutorial: Perakuan Kementerian"
          onClick={() => openInNewTab(TUTORIAL_CERTIFY_URL)}
        />
        {registrationForm && (
          <ResourceLink
            icon={mdiFileDownloadOutline}
            iconColor="#06D6A0"
            label="Muat turun borang permohonan akses"
            onClick={() => openInNewTab(registrationForm)}
          />
        )}
      </div>
    </React.Fragment>
  );
}

export default SignIn;
